import { useState, useMemo, useCallback } from "react";
import {
  DEFAULT_BULK_UPLOADS_FILTERS,
  DEFAULT_BULK_UPLOADS_PAGINATION,
} from "../constants/bulkUploadsConstants";

export const useBulkUploadsFilters = (isSuperAdmin = false) => {
  const [filters, setFilters] = useState(DEFAULT_BULK_UPLOADS_FILTERS);
  const [merchantId, setMerchantId] = useState("");
  const [pagination, setPagination] = useState(DEFAULT_BULK_UPLOADS_PAGINATION);

  const apiFilters = useMemo(() => {
    const { searchQuery, dateRange, status } = filters;

    return {
      searchQuery: searchQuery || "",
      startDate: dateRange?.startDate || "",
      endDate: dateRange?.endDate || "",
      status: status !== "all" ? status : "",
      page: pagination.pageIndex,
      size: pagination.pageSize,
    };
  }, [filters, pagination.pageIndex, pagination.pageSize]);

  const managementApiFilters = useMemo(() => {
    if (!isSuperAdmin) return apiFilters;
    const base = { ...apiFilters };
    if (merchantId) base.merchantId = merchantId;
    return base;
  }, [isSuperAdmin, apiFilters, merchantId]);

  const resetPagination = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const setSearchQuery = useCallback(
    (query) => {
      setFilters((prev) => ({ ...prev, searchQuery: query }));
      resetPagination();
    },
    [resetPagination],
  );

  const setDateRange = useCallback(
    (rangeOutput) => {
      setFilters((prev) => ({
        ...prev,
        dateRange: {
          startDate: rangeOutput?.startDate || null,
          endDate: rangeOutput?.endDate || null,
        },
      }));
      resetPagination();
    },
    [resetPagination],
  );

  const setStatus = useCallback(
    (value) => {
      setFilters((prev) => ({ ...prev, status: value || "all" }));
      resetPagination();
    },
    [resetPagination],
  );

  const onMerchantChange = useCallback(
    (value) => {
      setMerchantId(value || "");
      resetPagination();
    },
    [resetPagination],
  );

  return {
    filters,
    pagination,
    apiFilters,
    managementApiFilters,
    setPagination,
    resetPagination,
    searchQuery: filters.searchQuery,
    dateRange: filters.dateRange,
    status: filters.status,
    merchantId,
    setSearchQuery,
    setDateRange,
    setStatus,
    setMerchantId: onMerchantChange,
  };
};

export default useBulkUploadsFilters;
